import { useState, useEffect } from 'react'
import api, { getFileUrl } from '../utils/api'
import CreateStoryModal from './CreateStoryModal'
import ViewStoryModal from './ViewStoryModal'
import '../styles/storiesSection.css'

interface Story {
  id: string
  creator_email: string
  content: string
  media_type: string
  file_path?: string
  visibility_type: string
  created_at: string
  expires_at: string
}

interface ContactStories {
  creator_email: string
  stories: Story[]
  latestStory: Story
}

interface StoriesSectionProps {
  userEmail: string
}

export default function StoriesSection({ userEmail }: StoriesSectionProps) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedStory, setSelectedStory] = useState<Story | null>(null)
  const [selectedContactEmail, setSelectedContactEmail] = useState<string | null>(null)
  const [allStoriesArray, setAllStoriesArray] = useState<ContactStories[]>([])

  useEffect(() => {
    // Ensure userEmail is in localStorage
    if (userEmail && !localStorage.getItem('userEmail')) {
      localStorage.setItem('userEmail', userEmail)
      console.log('📧 Stored userEmail in localStorage:', userEmail)
    }
    
    loadStories()
    // Refresh stories every 30 seconds
    const interval = setInterval(loadStories, 30000)
    return () => clearInterval(interval)
  }, [userEmail])

  const loadStories = async () => {
    try {
      console.log('📚 Loading stories... userEmail:', localStorage.getItem('userEmail'))
      const response = await api.get('/api/stories')
      
      // Group ALL stories (including mine) by creator
      const storiesMapObj: Record<string, ContactStories> = {}
      const myStories = response.data.myStories || []
      const otherStories = response.data.otherStories || []
      const allStories = [...myStories, ...otherStories]
      
      allStories.forEach((story: Story) => {
        if (!storiesMapObj[story.creator_email]) {
          storiesMapObj[story.creator_email] = {
            creator_email: story.creator_email,
            stories: [],
            latestStory: story
          }
        }
        const contactStories = storiesMapObj[story.creator_email]
        contactStories.stories.push(story)
        // Keep track of the most recent story
        if (new Date(story.created_at) > new Date(contactStories.latestStory.created_at)) {
          contactStories.latestStory = story
        }
      })
      
      // Convert to array and sort by latest story
      const storiesArray = Object.values(storiesMapObj).sort((a, b) => 
        new Date(b.latestStory.created_at).getTime() - new Date(a.latestStory.created_at).getTime()
      )
      
      setAllStoriesArray(storiesArray)
    } catch (error) {
      console.error('Erreur lors du chargement des stories:', error)
    }
  }

  const handleStoryCreated = () => {
    setShowCreateModal(false)
    loadStories()
  }

  const handleViewStory = (story: Story) => {
    setSelectedStory(story)
    setSelectedContactEmail(story.creator_email)
    setShowViewModal(true)
    
    // Mark as viewed
    api.post(`/api/stories/${story.id}/views`).catch(console.error)
  }

  const handleCloseViewModal = () => {
    setShowViewModal(false)
    setSelectedStory(null)
    setSelectedContactEmail(null)
    loadStories() // Refresh to update viewers list
  }

  const handleNavigateStory = (story: Story) => {
    setSelectedStory(story)
    // Mark as viewed
    api.post(`/api/stories/${story.id}/views`).catch(console.error)
  }

  return (
    <div className="stories-section">
      <div className="stories-header">
        <h3>Stories</h3>
        <button 
          className="btn-add-story"
          onClick={() => setShowCreateModal(true)}
          title="Créer une story"
        >
          +
        </button>
      </div>

      <div className="stories-list">
        {/* Stories groupées par contact (y compris les miennes) */}
        {allStoriesArray.length > 0 ? (
          allStoriesArray.map((contactStories) => (
            <div 
              key={contactStories.creator_email}
              className={`story-item ${contactStories.creator_email === userEmail ? 'my-stories' : 'contact-story'}`}
              onClick={() => handleViewStory(contactStories.latestStory)}
              title={`${contactStories.creator_email} (${contactStories.stories.length} story(ies))`}
            >
              <div className="story-avatar">
                {contactStories.latestStory.media_type === 'image' && contactStories.latestStory.file_path && (
                  <img 
                    src={getFileUrl(contactStories.latestStory.file_path)} 
                    alt={contactStories.creator_email}
                    className="story-thumbnail"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://via.placeholder.com/56?text=${contactStories.creator_email.charAt(0).toUpperCase()}`;
                    }}
                  />
                )}
                {contactStories.latestStory.media_type === 'video' && contactStories.latestStory.file_path && (
                  <div className="story-video-thumb">
                    <span>🎬</span>
                  </div>
                )}
                {!contactStories.latestStory.file_path && (
                  <img 
                    src={`https://via.placeholder.com/56?text=${contactStories.creator_email.charAt(0).toUpperCase()}`}
                    alt={contactStories.creator_email}
                  />
                )}
                {contactStories.stories.length > 1 && (
                  <span className="story-count">{contactStories.stories.length}</span>
                )}
                {contactStories.creator_email === userEmail && (
                  <span className="story-indicator">✓</span>
                )}
              </div>
              <div className="story-label">
                {contactStories.creator_email === userEmail ? 'Ma story' : contactStories.creator_email.split('@')[0]}
              </div>
            </div>
          ))
        ) : (
          <div 
            className="story-item empty-story"
            onClick={() => setShowCreateModal(true)}
          >
            <div className="story-avatar">
              <span className="story-icon">+</span>
            </div>
            <div className="story-label">Créer</div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateStoryModal 
          onClose={() => setShowCreateModal(false)}
          onCreated={handleStoryCreated}
          userEmail={userEmail}
        />
      )}

      {showViewModal && selectedStory && selectedContactEmail && (
        <ViewStoryModal 
          story={selectedStory}
          allContactStories={allStoriesArray.find(cs => cs.creator_email === selectedContactEmail)?.stories || []}
          onClose={handleCloseViewModal}
          onNavigate={handleNavigateStory}
          userEmail={userEmail}
        />
      )}
    </div>
  )
}
